<script setup lang="ts">
import { ref, watch } from 'vue';
import gettext from '@/i18n/gettext';
import type { CatalogueProject } from '@/models/Projects';
import { requiredRule, numericOnlyRule } from '@/helpers/FormRules';

const { $gettext } = gettext;

const props = defineProps<{
  modelValue: boolean;
  project?: CatalogueProject | null;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (
    e: 'save',
    payload: { name: string; dealId: string | null; uuid?: string },
  ): void;
}>();

const name = ref('');
const dealId = ref('');
const formValid = ref(false);

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      name.value = props.project?.name || '';
      dealId.value = props.project?.dealId || '';
    }
  },
);

const handleSave = () => {
  if (!formValid.value) {
    return;
  }
  const trimmedDealId = dealId.value.trim();
  emit('save', {
    name: name.value.trim(),
    dealId: trimmedDealId || null,
    uuid: props.project?.uuid,
  });
};
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="480"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title>
        {{ project ? $gettext('Edit Project') : $gettext('Create Project') }}
      </v-card-title>
      <v-card-text>
        <v-form v-model="formValid">
          <v-text-field
            v-model="name"
            :label="$gettext('Name')"
            :rules="[requiredRule]"
          />
          <v-text-field
            v-model="dealId"
            :label="$gettext('Deal ID')"
            :rules="[numericOnlyRule]"
            :placeholder="$gettext('Optional HubSpot Deal ID')"
            persistent-hint
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="$emit('update:modelValue', false)">
          {{ $gettext('Cancel') }}
        </v-btn>
        <v-btn color="primary" :disabled="!formValid" @click="handleSave">{{
          $gettext('Save')
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
